import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import type {
  DrawingRevisionGroup,
  DrawingRevisionOption,
  Highlight,
  PageClassification,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  documentPageKey,
  revisionOptionClassification,
  revisionOptionDocumentPage,
  revisionOptionPageNum,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/drawingRevisionOptions'

const NON_DRAWING_CATEGORIES = new Set(['OTHER', 'other'])

export type DocumentResultCardItem =
  | {
      kind: 'document'
      key: string
      document: DocumentTableRow
      highlights?: Highlight[]
    }
  | {
      kind: 'drawingPage'
      key: string
      document: DocumentTableRow
      sourceDocumentId: string
      pageNum: number
      category: string
      highlights: Highlight[]
      drawingNumber?: string
      sheetNumber?: string
      revisionCode?: string
      revisionOptions?: DrawingRevisionOption[]
      revisionStackingFailed?: boolean
    }

type DrawingPageCardItem = Extract<
  DocumentResultCardItem,
  { kind: 'drawingPage' }
>
type DrawingPageCardItemWithRevisionOptions = DrawingPageCardItem & {
  revisionOptions: DrawingRevisionOption[]
}

export function buildDocumentResultCardItems({
  documents,
  highlightsMap,
  pageClassificationsMap,
  drawingRevisionGroupsMap,
  documentRowsById,
  enablePageCardSurfacing = true,
}: {
  documents: DocumentTableRow[]
  highlightsMap: Map<string, Highlight[]>
  pageClassificationsMap: Map<string, PageClassification[]>
  drawingRevisionGroupsMap?: Map<string, DrawingRevisionGroup[]>
  documentRowsById?: Map<string, DocumentTableRow>
  enablePageCardSurfacing?: boolean
}): DocumentResultCardItem[] {
  if (!enablePageCardSurfacing) {
    return documents.map((document) => documentCardItem(document, []))
  }

  const items = documents.flatMap((document) =>
    buildCardItemsForDocument({
      document,
      highlights: highlightsMap.get(document.id) ?? [],
      pageClassifications: pageClassificationsMap.get(document.id) ?? [],
      drawingRevisionGroups: drawingRevisionGroupsMap?.get(document.id) ?? [],
    })
  )
  if (items.some(hasLoadedRevisionOptions)) {
    return mergeDrawingCardItemsByRevisionGroup(items, documentRowsById)
  }
  return items
}

function buildCardItemsForDocument({
  document,
  highlights,
  pageClassifications,
  drawingRevisionGroups,
}: {
  document: DocumentTableRow
  highlights: Highlight[]
  pageClassifications: PageClassification[]
  drawingRevisionGroups: DrawingRevisionGroup[]
}): DocumentResultCardItem[] {
  const highlightedPages = new Set(
    highlights.map((highlight) => highlight.page_num)
  )
  const drawingPageNums = new Set<number>()
  for (const group of drawingRevisionGroups) {
    drawingPageNums.add(group.page_num)
  }
  for (const classification of pageClassifications) {
    if (
      isDrawingCategory(classification.category) &&
      (highlightedPages.size === 0 ||
        highlightedPages.has(classification.page_num))
    ) {
      drawingPageNums.add(classification.page_num)
    }
  }

  if (highlightedPages.size === 0 && drawingPageNums.size === 0) {
    return [documentCardItem(document, highlights)]
  }

  const categoryByPage = new Map(
    pageClassifications.map((classification) => [
      classification.page_num,
      classification.category ?? 'Drawing',
    ])
  )
  const sheetNumberByPage = new Map(
    pageClassifications
      .filter((classification) => classification.sheet_number?.trim())
      .map((classification) => [
        classification.page_num,
        classification.sheet_number?.trim() ?? '',
      ])
  )
  const drawingNumberByPage = new Map(
    pageClassifications
      .filter((classification) => classification.drawing_number?.trim())
      .map((classification) => [
        classification.page_num,
        classification.drawing_number?.trim() ?? '',
      ])
  )
  const revisionCodeByPage = new Map(
    pageClassifications
      .filter((classification) => classification.revision_code?.trim())
      .map((classification) => [
        classification.page_num,
        classification.revision_code?.trim() ?? '',
      ])
  )
  const revisionGroupByPage = new Map(
    drawingRevisionGroups.map((group) => [group.page_num, group])
  )

  const drawingItems = Array.from(drawingPageNums)
    .sort((a, b) => a - b)
    .map((pageNum): DocumentResultCardItem => {
      const revisionGroup = revisionGroupByPage.get(pageNum)
      const drawingNumber =
        revisionGroup?.drawing_number ?? drawingNumberByPage.get(pageNum)
      const sheetNumber =
        revisionGroup?.sheet_number ?? sheetNumberByPage.get(pageNum)
      return {
        kind: 'drawingPage',
        key: `${document.id}:drawing-page:${drawingNumber ?? pageNum}:${sheetNumber ?? ''}:${pageNum}`,
        document,
        sourceDocumentId: document.id,
        pageNum,
        category: categoryByPage.get(pageNum) ?? 'Drawing',
        highlights: highlights.filter(
          (highlight) => highlight.page_num === pageNum
        ),
        drawingNumber,
        sheetNumber,
        revisionCode: revisionCodeByPage.get(pageNum),
        revisionOptions:
          revisionGroup && revisionGroup.options.length > 1
            ? revisionGroup.options
            : undefined,
      }
    })

  const rolledUpHighlights = highlights.filter(
    (highlight) => !drawingPageNums.has(highlight.page_num)
  )
  if (rolledUpHighlights.length === 0) {
    return drawingItems
  }

  return [documentCardItem(document, rolledUpHighlights), ...drawingItems]
}

function documentCardItem(
  document: DocumentTableRow,
  highlights: Highlight[]
): DocumentResultCardItem {
  return {
    kind: 'document',
    key: document.id,
    document,
    highlights,
  }
}

function hasLoadedRevisionOptions(
  item: DocumentResultCardItem
): item is DrawingPageCardItemWithRevisionOptions {
  return item.kind === 'drawingPage' && (item.revisionOptions?.length ?? 0) > 1
}

function mergeDrawingCardItemsByRevisionGroup(
  items: DocumentResultCardItem[],
  documentRowsById?: Map<string, DocumentTableRow>
): DocumentResultCardItem[] {
  const mergedItems: DocumentResultCardItem[] = []
  const drawingItemsByRevisionGroupKey = new Map<
    string,
    DrawingPageCardItemWithRevisionOptions[]
  >()

  for (const item of items) {
    if (!hasLoadedRevisionOptions(item)) continue
    const revisionGroupKey = revisionOptionsGroupKey(item)
    drawingItemsByRevisionGroupKey.set(revisionGroupKey, [
      ...(drawingItemsByRevisionGroupKey.get(revisionGroupKey) ?? []),
      item,
    ])
  }

  const processedRevisionGroupKeys = new Set<string>()
  for (const item of items) {
    if (!hasLoadedRevisionOptions(item)) {
      mergedItems.push(item)
      continue
    }
    const revisionGroupKey = revisionOptionsGroupKey(item)

    if (processedRevisionGroupKeys.has(revisionGroupKey)) continue
    processedRevisionGroupKeys.add(revisionGroupKey)

    const drawingItems = drawingItemsByRevisionGroupKey.get(
      revisionGroupKey
    ) ?? [item]
    const revisionOptions = drawingItems.reduce<
      DrawingRevisionOption[] | undefined
    >(
      (options, drawingItem) =>
        mergeRevisionOptions(options, drawingItem.revisionOptions),
      undefined
    )

    if (!revisionOptions || revisionOptions.length <= 1) {
      mergedItems.push(...drawingItems)
      continue
    }

    if (canStackRevisionOptions(revisionOptions)) {
      const mergedItem = {
        ...item,
        key: `drawing-page:${revisionGroupKey}`,
        highlights: drawingItems.flatMap(
          (drawingItem) => drawingItem.highlights
        ),
        revisionOptions,
      }
      mergedItems.push(mergedItem)
      continue
    }

    const drawingItemByRevisionKey = new Map(
      drawingItems.map((drawingItem) => [
        documentPageKey(drawingItem.document.id, drawingItem.pageNum),
        drawingItem,
      ])
    )
    for (const option of revisionOptions) {
      const documentPage = revisionOptionDocumentPage(option)
      if (!documentPage) continue
      const classification = revisionOptionClassification(option)
      const matchingItem = drawingItemByRevisionKey.get(
        revisionOptionKey(option)
      )
      const hydratedRevisionDocument =
        documentRowsById?.get(documentPage.documentId) ?? matchingItem?.document
      if (!hydratedRevisionDocument && !matchingItem) continue
      const sourceDocument = matchingItem?.document ?? item.document
      mergedItems.push({
        ...(matchingItem ?? item),
        key: `drawing-page-failed:${revisionGroupKey}:${revisionOptionKey(option)}`,
        document: revisionOptionToDocument(
          matchingItem?.document ?? item.document,
          option,
          hydratedRevisionDocument
        ),
        sourceDocumentId: sourceDocument.id,
        pageNum: documentPage.pageNum,
        highlights: matchingItem?.highlights ?? [],
        drawingNumber: classification?.drawing_number ?? undefined,
        sheetNumber: classification?.sheet_number ?? undefined,
        revisionCode: classification?.revision_code ?? undefined,
        revisionOptions: undefined,
        revisionStackingFailed: true,
      })
    }
  }

  return mergedItems
}

function revisionOptionsGroupKey(
  item: DrawingPageCardItemWithRevisionOptions
): string {
  return item.revisionOptions
    .map((option) => revisionOptionKey(option))
    .sort()
    .join('|')
}

function canStackRevisionOptions(options: DrawingRevisionOption[]): boolean {
  return (
    hasOnlyUniquePopulatedValues(
      options.map((option) => normalizeRevisionCode(option))
    ) &&
    hasOnlyUniquePopulatedValues(
      options.map((option) => normalizeRevisionDate(option))
    )
  )
}

function hasOnlyUniquePopulatedValues(values: string[]): boolean {
  return values.every(Boolean) && new Set(values).size === values.length
}

function mergeRevisionOptions(
  firstOptions: DrawingRevisionOption[] | undefined,
  secondOptions: DrawingRevisionOption[] | undefined
): DrawingRevisionOption[] | undefined {
  const optionsByKey = new Map<string, DrawingRevisionOption>()
  for (const option of [...(firstOptions ?? []), ...(secondOptions ?? [])]) {
    optionsByKey.set(revisionOptionKey(option), option)
  }
  const options = sortRevisionOptions(Array.from(optionsByKey.values())) ?? []
  return options.length > 1 ? options : undefined
}

function sortRevisionOptions(
  options: DrawingRevisionOption[] | undefined
): DrawingRevisionOption[] | undefined {
  if (!options || options.length <= 1) return options
  return [...options].sort((a, b) => {
    const dateCompare = compareNullableStringsDesc(
      revisionOptionClassification(a)?.revision_date,
      revisionOptionClassification(b)?.revision_date
    )
    if (dateCompare !== 0) return dateCompare
    return compareNullableStringsDesc(
      revisionOptionClassification(a)?.revision_code,
      revisionOptionClassification(b)?.revision_code
    )
  })
}

function compareNullableStringsDesc(
  first: string | null | undefined,
  second: string | null | undefined
): number {
  return (second ?? '').localeCompare(first ?? '')
}

function normalizeRevisionCode(option: DrawingRevisionOption): string {
  return (
    revisionOptionClassification(option)?.revision_code?.trim().toUpperCase() ??
    ''
  )
}

function normalizeRevisionDate(option: DrawingRevisionOption): string {
  return revisionOptionClassification(option)?.revision_date?.trim() ?? ''
}

export function revisionOptionKey(option: DrawingRevisionOption): string {
  return documentPageKey(
    option.document_id,
    revisionOptionClassification(option)?.page_num ?? 'unknown'
  )
}

export function resolveDrawingRevisionDisplay({
  hostDocument,
  hostPageNum,
  hostDrawingNumber,
  hostSheetNumber,
  hostRevisionCode,
  selectedRevision,
  documentRowsById,
}: {
  hostDocument: DocumentTableRow
  hostPageNum: number
  hostDrawingNumber?: string
  hostSheetNumber?: string
  hostRevisionCode?: string
  selectedRevision: DrawingRevisionOption | undefined
  documentRowsById: Map<string, DocumentTableRow>
}): {
  document: DocumentTableRow
  pageNum: number | undefined
  thumbnailBqDocumentId: string
} {
  const hostThumbnailId = hostDocument.bqDocumentId ?? hostDocument.id
  const classification = revisionOptionClassification(selectedRevision)
  const withHostMetadata = (document: DocumentTableRow): DocumentTableRow =>
    withDrawingPageMetadata(document, {
      drawingNumber: classification?.drawing_number ?? hostDrawingNumber,
      sheetNumber: classification?.sheet_number ?? hostSheetNumber,
      revisionCode: classification?.revision_code ?? hostRevisionCode,
      revisionDate: classification?.revision_date,
    })
  if (!selectedRevision) {
    return {
      document: withHostMetadata(hostDocument),
      pageNum: hostPageNum,
      thumbnailBqDocumentId: hostThumbnailId,
    }
  }
  const documentPage = revisionOptionDocumentPage(selectedRevision)
  if (!documentPage) {
    return {
      document: withHostMetadata(hostDocument),
      pageNum: undefined,
      thumbnailBqDocumentId: hostThumbnailId,
    }
  }
  const hydratedRevisionDocument = documentRowsById.get(documentPage.documentId)
  return {
    document: withHostMetadata(
      revisionOptionToDocument(
        hostDocument,
        selectedRevision,
        hydratedRevisionDocument
      )
    ),
    pageNum: documentPage.pageNum,
    thumbnailBqDocumentId: documentPage.documentId,
  }
}

export function revisionOptionToDocument(
  document: DocumentTableRow,
  option: DrawingRevisionOption,
  hydratedRevisionDocument?: DocumentTableRow
): DocumentTableRow {
  const documentPage = revisionOptionDocumentPage(option)
  const classification = revisionOptionClassification(option)
  const matchingHydrated =
    documentPage &&
    hydratedRevisionDocument &&
    (hydratedRevisionDocument.id === documentPage.documentId ||
      hydratedRevisionDocument.bqDocumentId === documentPage.documentId)
      ? hydratedRevisionDocument
      : undefined
  return withDrawingPageMetadata(matchingHydrated ?? document, {
    drawingNumber: classification?.drawing_number,
    sheetNumber: classification?.sheet_number,
    revisionCode: classification?.revision_code,
    revisionDate: classification?.revision_date,
  })
}

export function withDrawingPageMetadata(
  document: DocumentTableRow,
  options: {
    drawingNumber?: string | null
    sheetNumber?: string | null
    revisionCode?: string | null
    revisionDate?: string | null
  }
): DocumentTableRow {
  const drawingNumber = options.drawingNumber?.trim()
  const sheetNumber = options.sheetNumber?.trim()
  return {
    ...document,
    drawing: drawingNumber || document.drawing,
    sheetNumber: sheetNumber || document.sheetNumber || null,
    revisionCode: options.revisionCode ?? document.revisionCode,
    revisionDate: options.revisionDate ?? document.revisionDate,
  }
}

function retrievedRevisionOption(
  item: DocumentResultCardItem
): DrawingRevisionOption | undefined {
  if (item.kind !== 'drawingPage' || !item.revisionOptions) return undefined
  const retrievedKey = documentPageKey(item.document.id, item.pageNum)
  return (
    item.revisionOptions.find(
      (option) => revisionOptionKey(option) === retrievedKey
    ) ?? item.revisionOptions[0]
  )
}

export function resolveDocumentResultCardDisplay({
  item,
  selectedRevision,
  documentRowsById,
}: {
  item: DocumentResultCardItem
  selectedRevision?: DrawingRevisionOption
  documentRowsById?: Map<string, DocumentTableRow>
}): {
  document: DocumentTableRow
  pageNumber: number
  isPageCard: boolean
  drawingNumber?: string
  sheetNumber?: string
  revisionCode?: string
  revisionStackingFailed: boolean
  revisionOptions?: DrawingRevisionOption[]
  selectedRevisionKey?: string
  thumbnailBqDocumentId: string
} {
  const isPageCard = item.kind === 'drawingPage'
  const effectiveRevision = isPageCard
    ? (selectedRevision ?? retrievedRevisionOption(item))
    : undefined
  const classification = revisionOptionClassification(effectiveRevision)
  const drawingNumber = isPageCard
    ? (classification?.drawing_number ?? item.drawingNumber)
    : undefined
  const sheetNumber = isPageCard
    ? (classification?.sheet_number ?? item.sheetNumber)
    : undefined
  const revisionCode = isPageCard
    ? (classification?.revision_code ?? item.revisionCode)
    : undefined

  let document = item.document
  if (effectiveRevision) {
    document = revisionOptionToDocument(
      item.document,
      effectiveRevision,
      documentRowsById?.get(effectiveRevision.document_id)
    )
  } else if (isPageCard) {
    document = withDrawingPageMetadata(item.document, {
      drawingNumber,
      sheetNumber,
    })
  }

  const pageNumber = isPageCard
    ? effectiveRevision
      ? revisionOptionPageNum(effectiveRevision, item.pageNum)
      : item.pageNum
    : 1

  return {
    document,
    pageNumber,
    isPageCard,
    drawingNumber,
    sheetNumber: sheetNumber ?? undefined,
    revisionCode: revisionCode ?? undefined,
    revisionStackingFailed: isPageCard && Boolean(item.revisionStackingFailed),
    revisionOptions: isPageCard ? item.revisionOptions : undefined,
    selectedRevisionKey: effectiveRevision
      ? revisionOptionKey(effectiveRevision)
      : undefined,
    thumbnailBqDocumentId:
      (effectiveRevision
        ? revisionOptionDocumentPage(effectiveRevision)?.documentId
        : undefined) ??
      item.document.bqDocumentId ??
      item.document.id,
  }
}

function isDrawingCategory(category: string | null | undefined): boolean {
  if (!category) return false
  return !NON_DRAWING_CATEGORIES.has(category)
}
